/**
 * RWK WebAPI Client - Generated from OpenAPI Specification
 * Comprehensive API client for the rifle club management system
 */

const _apiRoot = (window.ENV && window.ENV.API_URL) || 'https://localhost:7189';
const API_BASE = `${_apiRoot}/api`;
const AUTH_BASE = `${_apiRoot}/auth`;
const DEFAULT_GET_CACHE_TTL_MS = 45000;

const getCache = new Map();
const inFlightGetRequests = new Map();

function getTokenSnapshot() {
  return localStorage.getItem('authToken') || '';
}

function buildGetCacheKey(endpoint, withAuth, tokenSnapshot) {
  return `${withAuth ? 'auth' : 'noauth'}|${tokenSnapshot}|${endpoint}`;
}

function invalidateGetCache(match) {
  if (!match) {
    getCache.clear();
    inFlightGetRequests.clear();
    return;
  }

  [...getCache.keys()].forEach((key) => {
    if (key.includes(match)) getCache.delete(key);
  });

  [...inFlightGetRequests.keys()].forEach((key) => {
    if (key.includes(match)) inFlightGetRequests.delete(key);
  });
}

/**
 * Meldet ab, wenn die API die Sitzung verwirft (HTTP 401).
 *
 * Seit der serverseitigen Sitzungsprüfung können Token vor ihrem Ablauf
 * ungültig werden – etwa nach einer Passwortänderung, einem Rechteentzug,
 * einer Deaktivierung oder dem Widerruf eines Freigabelinks. Ohne diese
 * Behandlung würde die Anwendung mit einem toten Token weiterlaufen und in
 * jeder Ansicht Fehler zeigen.
 */
function handleUnauthorized() {
  if (!localStorage.getItem('authToken')) return;

  localStorage.removeItem('authToken');
  localStorage.removeItem('currentUser');
  invalidateGetCache();

  const path = window.location.pathname;
  if (path !== '/' && path !== '/login') {
    window.location.href = '/login';
  }
}

/**
 * Helper function to handle API responses
 */
function handleResponse(res, { skipAuthRedirect = false } = {}) {
  if (!res.ok) {
    if (res.status === 401 && !skipAuthRedirect) {
      handleUnauthorized();
    }
    return res.text().then((text) => {
      if (!text) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      let err;
      try {
        err = JSON.parse(text);
      } catch {
        throw new Error(text || `HTTP ${res.status}: ${res.statusText}`);
      }
      throw new Error(err.message || err.error_description || err.title || `HTTP ${res.status}: ${res.statusText}`);
    });
  }

  if (res.status === 204) {
    return Promise.resolve(null);
  }

  const contentLength = res.headers.get('content-length');
  if (contentLength === '0') {
    return Promise.resolve(null);
  }

  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    return res.json().catch(() => null);
  }

  return res.text().then((text) => text || null);
}

/**
 * Build standard request headers and optionally attach JWT Bearer token
 */
function buildHeaders(includeAuth = true) {
  const headers = { 'Content-Type': 'application/json' };
  if (includeAuth) {
    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  return headers;
}

/**
 * Helper function to make POST requests
 */
function postRequest(endpoint, data, options = {}) {
  const { withAuth = true, invalidateCache = true, skipAuthRedirect = false } = options;
  return fetch(endpoint, {
    method: 'POST',
    headers: buildHeaders(withAuth),
    body: JSON.stringify(data),
  }).then((res) => handleResponse(res, { skipAuthRedirect })).then((result) => {
    if (invalidateCache) {
      invalidateGetCache();
    }
    return result;
  });
}

/**
 * Helper function to make POST requests with x-www-form-urlencoded payload
 */
function postFormRequest(endpoint, data, options = {}) {
  const { withAuth = true, invalidateCache = false, skipAuthRedirect = false } = options;
  const headers = buildHeaders(withAuth);
  headers['Content-Type'] = 'application/x-www-form-urlencoded';

  return fetch(endpoint, {
    method: 'POST',
    headers,
    body: new URLSearchParams(data).toString(),
  }).then((res) => handleResponse(res, { skipAuthRedirect })).then((result) => {
    if (invalidateCache) {
      invalidateGetCache();
    }
    return result;
  });
}

/**
 * Helper function to make GET requests
 */
function getRequest(endpoint, options = {}) {
  const {
    withAuth = true,
    cache = true,
    ttlMs = DEFAULT_GET_CACHE_TTL_MS,
    forceRefresh = false,
  } = options;

  const tokenSnapshot = withAuth ? getTokenSnapshot() : '';
  const cacheKey = buildGetCacheKey(endpoint, withAuth, tokenSnapshot);

  if (cache && !forceRefresh) {
    const cached = getCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return Promise.resolve(cached.data);
    }

    if (inFlightGetRequests.has(cacheKey)) {
      return inFlightGetRequests.get(cacheKey);
    }
  }

  const request = fetch(endpoint, {
    headers: buildHeaders(withAuth),
  }).then(handleResponse).then((result) => {
    if (cache) {
      getCache.set(cacheKey, {
        data: result,
        expiresAt: Date.now() + ttlMs,
      });
    }
    return result;
  }).finally(() => {
    inFlightGetRequests.delete(cacheKey);
  });

  if (cache) {
    inFlightGetRequests.set(cacheKey, request);
  }

  return request;
}

/**
 * Helper function to make DELETE requests
 */
function deleteRequest(endpoint, options = {}) {
  const { withAuth = true, invalidateCache = true } = options;
  return fetch(endpoint, {
    method: 'DELETE',
    headers: buildHeaders(withAuth),
  }).then(handleResponse).then((result) => {
    if (invalidateCache) {
      invalidateGetCache();
    }
    return result;
  });
}

// ===== Authentication =====
export const Auth = {
  login: (username, password, rememberMe = false) =>
    postFormRequest(
      `${AUTH_BASE}/token`,
      { grant_type: 'password', username, password, scope: 'api', remember_me: rememberMe ? 'true' : 'false' },
      // Ein fehlgeschlagener Login darf keine Abmeldung auslösen – die Seite
      // zeigt die Fehlermeldung selbst an.
      { withAuth: false, skipAuthRedirect: true }
    ),
  refresh: () =>
    postRequest(`${AUTH_BASE}/refresh`, {}, { invalidateCache: false }),
  changePassword: (oldPassword, newPassword) =>
    postRequest(`${AUTH_BASE}/change-password`, { oldPassword, newPassword }),
  resetPassword: (schuetzenId) =>
    postRequest(`${AUTH_BASE}/reset-password`, { schuetzenId }),
  getProfil: () =>
    getRequest(`${AUTH_BASE}/profil`, { cache: false }),
  updateProfil: (data) =>
    postRequest(`${AUTH_BASE}/update-profil`, data),
};

// ===== Auswertungen (Aggregates/Statistics) =====
export const Auswertungen = {
  getEinzelBySaison: (saisonId) => getRequest(`${API_BASE}/Auswertungen/einzel/saison/${saisonId}`),
  getEinzelByKlasse: (klassenId) => getRequest(`${API_BASE}/Auswertungen/einzel/klasse/${klassenId}`),
  getEinzelBySaisonUndWettkampfart: (saisonId, waffenartId, anschlagsartId) =>
    getRequest(`${API_BASE}/Auswertungen/einzel/saison/${saisonId}/wettkampfart/${waffenartId}/${anschlagsartId}`),
  getEinzelBySchuetzeUndSaison: (schuetzenId, saisonId) =>
    getRequest(`${API_BASE}/Auswertungen/einzel/schuetze/${schuetzenId}/saison/${saisonId}`),
  getMannschaftBySaison: (saisonId) => getRequest(`${API_BASE}/Auswertungen/mannschaft/saison/${saisonId}`),
  getMannschaftByKlasse: (klassenId) => getRequest(`${API_BASE}/Auswertungen/mannschaft/klasse/${klassenId}`),
  getMannschaftBySaisonUndWettkampfart: (saisonId, waffenartId, anschlagsartId) =>
    getRequest(`${API_BASE}/Auswertungen/mannschaft/saison/${saisonId}/wettkampfart/${waffenartId}/${anschlagsartId}`),
  getMannschaftByTeamUndSaison: (mannschaftsId, saisonId) =>
    getRequest(`${API_BASE}/Auswertungen/mannschaft/mannschaft/${mannschaftsId}/saison/${saisonId}`),
  getKlassenBericht: (klassenId) => getRequest(`${API_BASE}/Auswertungen/klasse/${klassenId}/bericht`),
};

// ===== Altersklassen (Age Classes) =====
export const Altersklassen = {
  getAll: () => getRequest(`${API_BASE}/Altersklassen`),
  getById: (id) => getRequest(`${API_BASE}/Altersklassen/${id}`),
  create: (data) => postRequest(`${API_BASE}/Altersklassen/create`, data),
  update: (data) => postRequest(`${API_BASE}/Altersklassen/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Altersklassen/delete`, data),
};


// ===== Anschlagsarten (Shooting Positions) =====
export const Anschlagsarten = {
  getAll: () => getRequest(`${API_BASE}/Anschlagsarten`),
  getById: (id) => getRequest(`${API_BASE}/Anschlagsarten/${id}`),
  create: (data) => postRequest(`${API_BASE}/Anschlagsarten/create`, data),
  update: (data) => postRequest(`${API_BASE}/Anschlagsarten/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Anschlagsarten/delete`, data),
};

// ===== Waffenarten (Weapon Types) =====
export const Waffenarten = {
  getAll: () => getRequest(`${API_BASE}/Waffenarten`),
  getById: (id) => getRequest(`${API_BASE}/Waffenarten/${id}`),
  create: (data) => postRequest(`${API_BASE}/Waffenarten/create`, data),
  update: (data) => postRequest(`${API_BASE}/Waffenarten/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Waffenarten/delete`, data),
};

// ===== ErgebnisseEinzel (Individual Results) =====
export const ErgebnisseEinzel = {
  getByKlasse: (klassenId) => getRequest(`${API_BASE}/ErgebnisseEinzel/klasse/${klassenId}`),
  getByWettkampf: (wettkampfId) => getRequest(`${API_BASE}/ErgebnisseEinzel/wettkampf/${wettkampfId}`),
  getBySchuetze: (schuetzenId) => getRequest(`${API_BASE}/ErgebnisseEinzel/schuetze/${schuetzenId}`),
  getById: (id) => getRequest(`${API_BASE}/ErgebnisseEinzel/${id}`),
  create: (data) => postRequest(`${API_BASE}/ErgebnisseEinzel/create`, data),
  update: (data) => postRequest(`${API_BASE}/ErgebnisseEinzel/update`, data),
  delete: (data) => postRequest(`${API_BASE}/ErgebnisseEinzel/delete`, data),
};

// ===== ErgebnisseMannschaft (Team Results) =====
export const ErgebnisseMannschaft = {
  getByKlasse: (klassenId) => getRequest(`${API_BASE}/ErgebnisseMannschaft/klasse/${klassenId}`),
  getByKlassenUndWettkampf: (klassenId, wettkampfId) => getRequest(`${API_BASE}/ErgebnisseMannschaft/klasse/${klassenId}/wettkampf/${wettkampfId}`),
  getByWettkampf: (wettkampfId) => getRequest(`${API_BASE}/ErgebnisseMannschaft/wettkampf/${wettkampfId}`),
  getBySaison: (saisonId) => getRequest(`${API_BASE}/ErgebnisseMannschaft/saison/${saisonId}`),
  getById: (id) => getRequest(`${API_BASE}/ErgebnisseMannschaft/${id}`),
  create: (data) => postRequest(`${API_BASE}/ErgebnisseMannschaft/create`, data),
  update: (data) => postRequest(`${API_BASE}/ErgebnisseMannschaft/update`, data),
  delete: (data) => postRequest(`${API_BASE}/ErgebnisseMannschaft/delete`, data),
};

// ===== Funktionen (Functions/Roles) =====
export const Funktionen = {
  getAll: () => getRequest(`${API_BASE}/Funktionen`),
  getById: (id) => getRequest(`${API_BASE}/Funktionen/${id}`),
  getSchuetzen: (id) => getRequest(`${API_BASE}/Funktionen/${id}/schuetzen`, { cache: false }),
  create: (data) => postRequest(`${API_BASE}/Funktionen/create`, data),
  update: (data) => postRequest(`${API_BASE}/Funktionen/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Funktionen/delete`, data),
};

// ===== Gebuehren (Fees) =====
export const Gebuehren = {
  getAll: () => getRequest(`${API_BASE}/Gebuehren`),
  getById: (id) => getRequest(`${API_BASE}/Gebuehren/${id}`),
  create: (data) => postRequest(`${API_BASE}/Gebuehren/create`, data),
  update: (data) => postRequest(`${API_BASE}/Gebuehren/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Gebuehren/delete`, data),
};

// ===== Gebuehrengruende (Fee Reasons) =====
export const Gebuehrengruende = {
  getAll: () => getRequest(`${API_BASE}/Gebuehrengruende`),
  getById: (id) => getRequest(`${API_BASE}/Gebuehrengruende/${id}`),
  create: (data) => postRequest(`${API_BASE}/Gebuehrengruende/create`, data),
  update: (data) => postRequest(`${API_BASE}/Gebuehrengruende/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Gebuehrengruende/delete`, data),
};

// ===== Rechnungen (Invoices) =====
export const Rechnungen = {
  getAll: () => getRequest(`${API_BASE}/Rechnungen`),
  getById: (id) => getRequest(`${API_BASE}/Rechnungen/${id}`),
  create: (data) => postRequest(`${API_BASE}/Rechnungen/create`, data),
  update: (data) => postRequest(`${API_BASE}/Rechnungen/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Rechnungen/delete`, data),
  // Vorbelegung des Versanddialogs und Versandprotokoll (nicht cachen: ändert sich mit jedem Versand)
  // art: 'rechnung' (Standard) | 'erinnerung' | 'mahnung1' | 'mahnung2'
  getVersand: (id, art = 'rechnung') =>
    getRequest(`${API_BASE}/Rechnungen/${id}/versand?art=${encodeURIComponent(art)}`, { cache: false }),
  versenden: (id, data) => postRequest(`${API_BASE}/Rechnungen/${id}/versenden`, data),
  getVersandVorschlaege: (rechnungIds) =>
    postRequest(`${API_BASE}/Rechnungen/versand-vorschlaege`, { rechnungIds }, { invalidateCache: false }),
  // Betreff/Text mit eingesetzten Platzhaltern für eine Rechnung: { betreff, nachrichtHtml }
  getVersandVorschau: (id, data) =>
    postRequest(`${API_BASE}/Rechnungen/${id}/versand-vorschau`, data, { invalidateCache: false }),
};

// ===== Kalender (Termine, eigene Wettkämpfe und Saison-Eckdaten) =====

/** Arten eines Kalendereintrags – müssen zu KalenderArt der API passen. */
export const KALENDER_ART = {
  TERMIN: 'termin',
  WETTKAMPF: 'wettkampf',
  WETTKAMPF_ANSPRECHPARTNER: 'wettkampf_ansprechpartner',
  WETTKAMPF_HEIM: 'wettkampf_heim',
  WETTKAMPF_AUSWAERTS: 'wettkampf_auswaerts',
  SAISON: 'saison',
  MELDUNG: 'meldung',
};

export const Kalender = {
  /** Alle Kalendereinträge des angemeldeten Benutzers, aufsteigend nach Zeitpunkt. */
  getMeine: () => getRequest(`${API_BASE}/Kalender/meine`, { cache: false }),
  /** Alle Kalendereinträge eines Vereins (Wettkämpfe aller Mannschaften, Vereinstermine, Saison-Eckdaten). */
  getVerein: (vereinsId) => getRequest(`${API_BASE}/Kalender/verein/${vereinsId}`, { cache: false }),
};

// ===== Klassen (Classes) =====
export const Klassen = {
  getBySaison: (saisonId) => getRequest(`${API_BASE}/Klassen/saison/${saisonId}`),
  getById: (id) => getRequest(`${API_BASE}/Klassen/${id}`),
  create: (data) => postRequest(`${API_BASE}/Klassen/create`, data),
  createBatch: (data) => postRequest(`${API_BASE}/Klassen/create-batch`, data),
  update: (data) => postRequest(`${API_BASE}/Klassen/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Klassen/delete`, data),
  deleteBatch: (klassenIds) => postRequest(`${API_BASE}/Klassen/delete-batch`, { klassenIds }),
  autoSortierung: (saisonId, klassengruppenId = null) =>
    postRequest(`${API_BASE}/Klassen/auto-sortierung`, { saisonId, klassengruppenId }),
};

// ===== Klassengruppen (Class Groups) =====
export const Klassengruppen = {
  getAll: () => getRequest(`${API_BASE}/Klassengruppen`),
  getById: (id) => getRequest(`${API_BASE}/Klassengruppen/${id}`),
  create: (data) => postRequest(`${API_BASE}/Klassengruppen/create`, data),
  update: (data) => postRequest(`${API_BASE}/Klassengruppen/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Klassengruppen/delete`, data),
};

// ===== Mannschaften (Teams) =====
export const Mannschaften = {
  getBySaison: (saisonId) => getRequest(`${API_BASE}/Mannschaften/saison/${saisonId}`),
  getBySaisonUndKlassengruppe: (saisonId, klassengruppenId) =>
    getRequest(`${API_BASE}/Mannschaften/saison/${saisonId}/klassengruppe/${klassengruppenId}`),
  getBySaisonUndVerein: (saisonId, vereinsId) =>
    getRequest(`${API_BASE}/Mannschaften/saison/${saisonId}/verein/${vereinsId}`),
  getByVerein: (vereinsId) => getRequest(`${API_BASE}/Mannschaften/verein/${vereinsId}`),
  getById: (id) => getRequest(`${API_BASE}/Mannschaften/${id}`),
  // Alle Daten der Mannschaftsübersicht in einem Aufruf (nur die benötigten Zeilen).
  getUebersicht: (id) => getRequest(`${API_BASE}/Mannschaften/${id}/uebersicht`),
  // "Meine Mannschaften" des angemeldeten Schützen (aktive Saison) in einem Aufruf.
  getMeine: () => getRequest(`${API_BASE}/Mannschaften/meine`),
  create: (data) => postRequest(`${API_BASE}/Mannschaften/create`, data),
  createBatch: (data) => postRequest(`${API_BASE}/Mannschaften/create-batch`, data),
  update: (data) => postRequest(`${API_BASE}/Mannschaften/update`, data),
  updateBatch: (data) => postRequest(`${API_BASE}/Mannschaften/update-batch`, data),
  // Ändert nur Heimstand und Ansprechpartner – jederzeit erlaubt, auch nach der Meldung.
  kontaktAktualisieren: (data) => postRequest(`${API_BASE}/Mannschaften/kontakt-aktualisieren`, data),
  delete: (data) => postRequest(`${API_BASE}/Mannschaften/delete`, data),
  deleteBatch: (mannschaftsIds) => postRequest(`${API_BASE}/Mannschaften/delete-batch`, { mannschaftsIds }),
  // Ersatz für delete während einer aktiven Saison: Status -> abgemeldet, Wettkämpfe -> gestrichen, Gebühr gebucht.
  deaktivieren: (data) => postRequest(`${API_BASE}/Mannschaften/deaktivieren`, data),
  meldungZurueckziehen: (saisonId, vereinsId) => postRequest(`${API_BASE}/Mannschaften/meldung-zurueckziehen`, { saisonId, vereinsId }),
  getStandkonflikte: (saisonId) => getRequest(`${API_BASE}/Mannschaften/standkonflikte/${saisonId}`),
};

// ===== MannschaftsMitgliedschaften (Team Memberships) =====
export const MannschaftsMitgliedschaften = {
  getByMannschaft: (mannschaftsId) => getRequest(`${API_BASE}/MannschaftsMitgliedschaften/mannschaft/${mannschaftsId}`),
  getBySchuetze: (schuetzenId) => getRequest(`${API_BASE}/MannschaftsMitgliedschaften/schuetze/${schuetzenId}`),
  getById: (id) => getRequest(`${API_BASE}/MannschaftsMitgliedschaften/${id}`),
  create: (data) => postRequest(`${API_BASE}/MannschaftsMitgliedschaften/create`, data),
  createBatch: (data) => postRequest(`${API_BASE}/MannschaftsMitgliedschaften/create-batch`, data),
  update: (data) => postRequest(`${API_BASE}/MannschaftsMitgliedschaften/update`, data),
  delete: (data) => postRequest(`${API_BASE}/MannschaftsMitgliedschaften/delete`, data),
  deleteBatch: (mannschaftsMitgliedschaftIds) =>
    postRequest(`${API_BASE}/MannschaftsMitgliedschaften/delete-batch`, { mannschaftsMitgliedschaftIds }),
  // Übernimmt den vollständigen Kader [{ schuetzenId, meldeergebnis }] in einem Vorgang.
  kaderSpeichern: (mannschaftsId, mitglieder) =>
    postRequest(`${API_BASE}/MannschaftsMitgliedschaften/kader-speichern`, { mannschaftsId, mitglieder }),
};

// ===== Meldungen (Per-Verein Meldungs-Status) =====
export const Meldungen = {
  getStatus: (saisonId, vereinsId) =>
    getRequest(`${API_BASE}/Meldungen/saison/${saisonId}/verein/${vereinsId}`, { cache: false }),
};

// ===== Mitteilungen und Termine (an Funktionsträger) =====

/** Mögliche Antworten. 'gelesen' = zur Kenntnis genommen. */
export const MITTEILUNG_ANTWORT = { GELESEN: 'gelesen', ZUGESAGT: 'zugesagt', ABGESAGT: 'abgesagt' };
export const MITTEILUNG_ART = { MITTEILUNG: 'mitteilung', TERMIN: 'termin' };

export const Mitteilungen = {
  /** Alle erhaltenen Einträge des angemeldeten Benutzers (inkl. beantworteter). */
  getMeine: () => getRequest(`${API_BASE}/Mitteilungen/meine`, { cache: false }),
  /** Noch nicht beantwortete Einträge – Grundlage für das Modal beim Seitenaufruf. */
  getOffen: () => getRequest(`${API_BASE}/Mitteilungen/offen`, { cache: false }),
  /** antwort: 'gelesen' | 'zugesagt' | 'abgesagt'. Ersetzt eine frühere Antwort. */
  antworten: (mitteilungsId, antwort = MITTEILUNG_ANTWORT.GELESEN) =>
    postRequest(`${API_BASE}/Mitteilungen/antworten`, { mitteilungsId, antwort }),
  downloadIcal: async (mitteilungsId, titel) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/Mitteilungen/${mitteilungsId}/ical`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const dispositionFilename = (() => {
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename\*?=(?:UTF-8''|")?([^;"']+)/i);
      return match ? decodeURIComponent(match[1]) : `RWK_Termin_${titel || mitteilungsId}.ics`;
    })();
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = dispositionFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },

  // --- Verwaltung (nur Administratoren) ---
  getAll: () => getRequest(`${API_BASE}/Mitteilungen`, { cache: false }),
  getById: (id) => getRequest(`${API_BASE}/Mitteilungen/${id}`, { cache: false }),
  getAntworten: (id) => getRequest(`${API_BASE}/Mitteilungen/${id}/antworten`, { cache: false }),
  create: (data) => postRequest(`${API_BASE}/Mitteilungen/create`, data),
  update: (data) => postRequest(`${API_BASE}/Mitteilungen/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Mitteilungen/delete`, data),

  // --- Anhänge ---
  uploadAnhang: (formData) => uploadFormData(`${API_BASE}/Mitteilungen/anhang/upload`, formData),
  deleteAnhang: (anhangId) => postRequest(`${API_BASE}/Mitteilungen/anhang/delete`, { anhangId }),
  downloadAnhang: async (anhangId, dateiname) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/Mitteilungen/anhang/${anhangId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = dateiname || `Anhang_${anhangId}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
};

// ===== Rollen (Roles) =====
export const Rollen = {
  getAll: () => getRequest(`${API_BASE}/Rollen`),
  getById: (id) => getRequest(`${API_BASE}/Rollen/${id}`),
  getSchuetzen: (id) => getRequest(`${API_BASE}/Rollen/${id}/schuetzen`, { cache: false }),
  create: (data) => postRequest(`${API_BASE}/Rollen/create`, data),
  update: (data) => postRequest(`${API_BASE}/Rollen/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Rollen/delete`, data),
};


// ===== Saisons (Seasons) =====
export const Saisons = {
  getAll: () => getRequest(`${API_BASE}/Saisons`),
  getById: (id) => getRequest(`${API_BASE}/Saisons/${id}`),
  create: (data) => postRequest(`${API_BASE}/Saisons/create`, data),
  update: (data) => postRequest(`${API_BASE}/Saisons/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Saisons/delete`, data),
};

// ===== Schuetzen (Shooters) =====
export const Schuetzen = {
  getAll: () => getRequest(`${API_BASE}/Schuetzen`),
  getByVereinUndKlassengruppe: (vereinsId, klassengruppenId, { stichtagOben, stichtagUnten } = {}) => {
    const params = new URLSearchParams();
    if (stichtagOben)  params.append('stichtagOben',  stichtagOben);
    if (stichtagUnten) params.append('stichtagUnten', stichtagUnten);
    const qs = params.toString();
    return getRequest(`${API_BASE}/Schuetzen/verein/${vereinsId}/klassengruppe/${klassengruppenId}${qs ? '?' + qs : ''}`);
  },
  getByVereinUndKlassengruppeUndDatum: (vereinsId, klassengruppenId, datum) =>
    getRequest(`${API_BASE}/Schuetzen/verein/${vereinsId}/klassengruppe/${klassengruppenId}/datum/${datum}`),
  getByVerein: (vereinsId) => getRequest(`${API_BASE}/Schuetzen/verein/${vereinsId}`),
  getBerechtigungenByVerein: (vereinsId) => getRequest(`${API_BASE}/Schuetzen/verein/${vereinsId}/berechtigungen`),
  // Berechtigungen des angemeldeten Benutzers (für alle Konten, nicht nur Administratoren).
  getEigeneBerechtigungen: () => getRequest(`${API_BASE}/Schuetzen/ich/berechtigungen`),
  getById: (id) => getRequest(`${API_BASE}/Schuetzen/${id}`),
  // Mehrere Schützen in einem Aufruf (höchstens 200 IDs).
  getMehrere: (ids) => {
    const eindeutig = [...new Set(ids)];
    if (eindeutig.length === 0) return Promise.resolve([]);
    const qs = eindeutig.map((id) => `ids=${encodeURIComponent(id)}`).join('&');
    return getRequest(`${API_BASE}/Schuetzen/mehrere?${qs}`);
  },
  create: (data) => postRequest(`${API_BASE}/Schuetzen/create`, data),
  update: (data) => postRequest(`${API_BASE}/Schuetzen/update`, data),
  setPasswort: (schuetzenId, neuesPasswort, passwortAktualisieren = true) =>
    postRequest(`${API_BASE}/Schuetzen/set-passwort`, { schuetzenId, neuesPasswort, passwortAktualisieren }),
  delete: (data) => postRequest(`${API_BASE}/Schuetzen/delete`, data),
  deaktivieren: (schuetzenId) => postRequest(`${API_BASE}/Schuetzen/deaktivieren`, { schuetzenId }),
  loginSperren: (schuetzenId) => postRequest(`${API_BASE}/Schuetzen/login-sperren`, { schuetzenId }),
  loginEntsperren: (schuetzenId) => postRequest(`${API_BASE}/Schuetzen/login-entsperren`, { schuetzenId }),
  // Nur Anschrift, Telefon und E-Mail – für Vereinsmanager der eigenen Mitglieder.
  kontaktAktualisieren: (data) => postRequest(`${API_BASE}/Schuetzen/kontakt-aktualisieren`, data),
};

// ===== Staende (Stands/Ranges) =====
export const Staende = {
  getAll: () => getRequest(`${API_BASE}/Staende`),
  getByVerein: (vereinsId) => getRequest(`${API_BASE}/Staende/verein/${vereinsId}`),
  getById: (id) => getRequest(`${API_BASE}/Staende/${id}`),
  create: (data) => postRequest(`${API_BASE}/Staende/create`, data),
  update: (data) => postRequest(`${API_BASE}/Staende/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Staende/delete`, data),
  geocodeAll: () => postRequest(`${API_BASE}/Staende/geocode-all`, {}),
  geocodeAddress: (data) => postRequest(`${API_BASE}/Staende/geocode`, data),
};

// ===== Trainingszeiten (Training times per Stand) =====
export const Trainingszeiten = {
  getByStand: (standId) => getRequest(`${API_BASE}/Trainingszeiten/stand/${standId}`),
  create: (data) => postRequest(`${API_BASE}/Trainingszeiten/create`, data),
  update: (data) => postRequest(`${API_BASE}/Trainingszeiten/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Trainingszeiten/delete`, data),
};

// ===== Termine (Events/Appointments) =====
export const Termine = {
  getAll: () => getRequest(`${API_BASE}/Termine`),
  getById: (id) => getRequest(`${API_BASE}/Termine/${id}`),
  create: (data) => postRequest(`${API_BASE}/Termine/create`, data),
  update: (data) => postRequest(`${API_BASE}/Termine/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Termine/delete`, data),
};

// ===== Vereine (Clubs) =====
export const Vereine = {
  getAll: () => getRequest(`${API_BASE}/Vereine`),
  getUebersicht: () => getRequest(`${API_BASE}/Vereine/uebersicht`),
  getById: (id) => getRequest(`${API_BASE}/Vereine/${id}`),
  /** Funktionsträger mit sichtbaren Kontaktdaten (Vereinsinfo). */
  getAnsprechpartner: (id) => getRequest(`${API_BASE}/Vereine/${id}/ansprechpartner`),
  create: (data) => postRequest(`${API_BASE}/Vereine/create`, data),
  update: (data) => postRequest(`${API_BASE}/Vereine/update`, data),
  delete: (data) => postRequest(`${API_BASE}/Vereine/delete`, data),
  /** Auswirkung einer Deaktivierung: { anzahlSchuetzen, hinderungsgrund }. */
  getDeaktivierung: (id) => getRequest(`${API_BASE}/Vereine/${id}/deaktivierung`, { cache: false }),
  deaktivieren: (vereinsId) => postRequest(`${API_BASE}/Vereine/deaktivieren`, { vereinsId }),
  aktivieren: (vereinsId) => postRequest(`${API_BASE}/Vereine/aktivieren`, { vereinsId }),
  /** Wettkampfverwalter des Vereins inkl. Obergrenze für den Vereinsmanager. */
  getWettkampfmanager: (vereinsId) => getRequest(`${API_BASE}/Vereine/${vereinsId}/wettkampfmanager`, { cache: false }),
  /** mannschaftId null = für den gesamten Verein. */
  addWettkampfmanager: (vereinsId, { schuetzenId, mannschaftId = null }) =>
    postRequest(`${API_BASE}/Vereine/${vereinsId}/wettkampfmanager/create`, { schuetzenId, mannschaftId }),
  removeWettkampfmanager: (vereinsId, scopedBerechtigungsId) =>
    postRequest(`${API_BASE}/Vereine/${vereinsId}/wettkampfmanager/delete`, { scopedBerechtigungsId }),
};

// ===== VereinsMitgliedschaften (Club Memberships) =====
export const VereinsMitgliedschaften = {
  getAll: () => getRequest(`${API_BASE}/VereinsMitgliedschaften`),
  // Nur die Mitgliedschaften des angemeldeten Schützen.
  getMeine: () => getRequest(`${API_BASE}/VereinsMitgliedschaften/meine`),
  getById: (id) => getRequest(`${API_BASE}/VereinsMitgliedschaften/${id}`),
  create: (data) => postRequest(`${API_BASE}/VereinsMitgliedschaften/create`, data),
  update: (data) => postRequest(`${API_BASE}/VereinsMitgliedschaften/update`, data),
  delete: ({ vereinsMitgliedschaftsId }) => postRequest(`${API_BASE}/VereinsMitgliedschaften/delete`, { vereinsMitgliedschaftsId }),
};

// ===== Share Tokens =====
export const ShareTokens = {
  create: (vereinsId, saisonId) =>
    postRequest(`${API_BASE}/share/mannschaftsmeldung`, { vereinsId, saisonId }),
  createSession: (shareToken) =>
    // Ein ungültiger Freigabelink zeigt eine eigene Meldung; keine Umleitung.
    postRequest(`${API_BASE}/share/session`, { shareToken }, { withAuth: false, skipAuthRedirect: true }),
  getVereinLinks: (saisonId) =>
    getRequest(`${API_BASE}/share/mannschaftsmeldung/links?saisonId=${encodeURIComponent(saisonId)}`, { cache: false }),
  /** Übersicht der ausgegebenen Links für die Verwaltungsseite (nur Admins). */
  getAusgegebeneLinks: ({ saisonId, vereinId, status, anzahl = 100, offset = 0 } = {}) => {
    const params = new URLSearchParams();
    if (saisonId) params.append('saisonId', saisonId);
    if (vereinId) params.append('vereinId', vereinId);
    if (status) params.append('status', status);
    params.append('anzahl', anzahl);
    params.append('offset', offset);
    const qs = params.toString();
    return getRequest(`${API_BASE}/share/mannschaftsmeldung/ausgegeben` + (qs ? `?${qs}` : ''), { cache: false });
  },
  widerrufen: (jti) =>
    postRequest(`${API_BASE}/share/mannschaftsmeldung/widerrufen`, { jti }),
};

/** Lädt eine Datei mit Token herunter; den Namen liefert der Content-Disposition-Header. */
async function downloadDatei(url, ersatzname) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  const disposition = res.headers.get('Content-Disposition') || '';
  const utf8Name = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const einfacherName = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
  const dateiname = utf8Name ? decodeURIComponent(utf8Name) : (einfacherName || ersatzname);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = dateiname;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

// ===== Wettkaempfe (Competitions) =====
export const Wettkaempfe = {
  getByKlasse: (klassenId) => getRequest(`${API_BASE}/Wettkaempfe/klasse/${klassenId}`),
  getBySaison: (saisonId) => getRequest(`${API_BASE}/Wettkaempfe/saison/${saisonId}`),
  getById: (id) => getRequest(`${API_BASE}/Wettkaempfe/${id}`),
  create: (data) => postRequest(`${API_BASE}/Wettkaempfe/create`, data),
  createBatch: (data) => postRequest(`${API_BASE}/Wettkaempfe/create-batch`, data),
  update: (data) => postRequest(`${API_BASE}/Wettkaempfe/update`, data),
  reaktivieren: (wettkampfId) => postRequest(`${API_BASE}/Wettkaempfe/reaktivieren`, { wettkampfId }),
  loescheErgebnisse: (wettkampfId) => postRequest(`${API_BASE}/Wettkaempfe/ergebnisse-loeschen`, { wettkampfId }),
  // Einzel- und Mannschaftsergebnisse in einem Vorgang (anlegen oder aktualisieren je Schütze/Mannschaft).
  ergebnisseSpeichern: (wettkampfId, einzel, mannschaften) =>
    postRequest(`${API_BASE}/Wettkaempfe/ergebnisse-speichern`, { wettkampfId, einzel, mannschaften }),
  delete: (data) => postRequest(`${API_BASE}/Wettkaempfe/delete`, data),
  deleteBatch: (wettkampfIds) => postRequest(`${API_BASE}/Wettkaempfe/delete-batch`, { wettkampfIds }),
  deleteBySaison: (saisonId) => postRequest(`${API_BASE}/Wettkaempfe/delete-by-saison`, { saisonId }),
  // ZIP mit je einer Meyton-Startliste (.msl) pro Heimwettkampf des Vereins.
  downloadMeytonStartlisten: (saisonId, vereinsId) =>
    downloadDatei(`${API_BASE}/Wettkaempfe/meyton/saison/${saisonId}/verein/${vereinsId}`, `Meyton_${saisonId}.zip`),
  // Meyton-Startliste (.msl) eines einzelnen Wettkampfs.
  downloadMeytonStartliste: (wettkampfId) =>
    downloadDatei(`${API_BASE}/Wettkaempfe/meyton/wettkampf/${wettkampfId}`, `Wettkampf_${wettkampfId}.msl`),
};

// ===== Wettkampf-Signaturen (digitale Bestätigung des Wettkampfberichts) =====
export const WettkampfSignaturen = {
  getByWettkampf: (wettkampfId) => getRequest(`${API_BASE}/WettkampfSignaturen/wettkampf/${wettkampfId}`),
  getAusstehend: () => getRequest(`${API_BASE}/WettkampfSignaturen/ausstehend`),
  getAusstehendAktuelleSaison: () => getRequest(`${API_BASE}/WettkampfSignaturen/ausstehend/aktuelle-saison`),
  sign: (data) => postRequest(`${API_BASE}/WettkampfSignaturen/sign`, data),
  ablehnen: (data) => postRequest(`${API_BASE}/WettkampfSignaturen/ablehnen`, data),
  reset: (data) => postRequest(`${API_BASE}/WettkampfSignaturen/reset`, data),
};

// ===== Push-Benachrichtigungen (Web Push, je Gerät) =====
export const Push = {
  getSchluessel: () => getRequest(`${API_BASE}/Push/schluessel`),
  /** Legt das Abonnement dieses Geräts samt gewählter Benachrichtigungsarten an bzw. aktualisiert es. */
  abonnieren: (data) => postRequest(`${API_BASE}/Push/abonnieren`, data, { invalidateCache: false }),
  /**
   * loeschen: true = Gerät will keine Benachrichtigungen mehr (endgültig entfernen),
   * false = Abmeldung aus der App (nur als abgemeldet markieren).
   * token: optional, für die Abmeldung, bei der das Token schon aus localStorage entfernt wird.
   */
  abmelden: (endpoint, { loeschen = false, token } = {}) => (token
    ? fetch(`${API_BASE}/Push/abmelden`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ endpoint, loeschen }),
    }).then((res) => handleResponse(res, { skipAuthRedirect: true }))
    : postRequest(`${API_BASE}/Push/abmelden`, { endpoint, loeschen }, { invalidateCache: false })),
  test: (endpoint) => postRequest(`${API_BASE}/Push/test`, { endpoint }, { invalidateCache: false }),
};

// ===== PDF =====
export const Pdf = {
  finalizeMeldung: (saisonId, vereinsId) =>
    postRequest(`${API_BASE}/pdf/meldung/saison/${saisonId}/verein/${vereinsId}/finalize`, {}),
  getMeldungStatus: (saisonId, vereinsId) =>
    getRequest(`${API_BASE}/pdf/meldung/saison/${saisonId}/verein/${vereinsId}/status`, { cache: false }),
  generateBrief: async ({ subject, body, vereinsId, documentId, sign = false }) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/pdf/brief`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ subject, body, vereinsId: vereinsId ?? null, documentId: documentId ?? null, sign }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `RWK_Brief_${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
  downloadMeldungPdf: async (saisonId, vereinsId) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/pdf/meldung/saison/${saisonId}/verein/${vereinsId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `RWK_Meldung_${saisonId}_${vereinsId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
  downloadRechnung: async (rechnungId, rechnungsnummer) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/pdf/rechnung/${rechnungId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `RWK_Rechnung_${rechnungsnummer || rechnungId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
  /**
   * Öffnet das Rechnungs-PDF im PDF-Viewer des Browsers (neuer Tab) statt es herunterzuladen.
   * Der Tab wird sofort geöffnet, damit der Popup-Blocker nicht greift; das PDF folgt nach dem Laden.
   */
  openRechnung: async (rechnungId) => {
    const viewer = window.open('', '_blank');
    if (viewer) viewer.document.title = 'Rechnung wird geladen…';
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/pdf/rechnung/${rechnungId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const blob = new Blob([await res.blob()], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      if (viewer) viewer.location.href = blobUrl;
      else window.location.href = blobUrl;
      // Blob-URL bewusst nicht freigeben: Speichern/Drucken im Viewer greift erneut darauf zu
    } catch (err) {
      viewer?.close();
      throw err;
    }
  },
  downloadWettkampfplan: async (saisonId) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/pdf/wettkampfplan/saison/${saisonId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const dispositionFilename = (() => {
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename\*?=(?:UTF-8''|")?([^;"']+)/i);
      return match ? decodeURIComponent(match[1]) : `RWK_Wettkampfplan_${saisonId}.pdf`;
    })();
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = dispositionFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
  downloadEinteilung: async (saisonId) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/pdf/einteilung/saison/${saisonId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const dispositionFilename = (() => {
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename\*?=(?:UTF-8''|")?([^;"']+)/i);
      return match ? decodeURIComponent(match[1]) : `RWK_Klasseneinteilung_${saisonId}.pdf`;
    })();
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = dispositionFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
  downloadAuswertung: async (klassenId) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/pdf/auswertung/klasse/${klassenId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const dispositionFilename = (() => {
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename\*?=(?:UTF-8''|")?([^;"']+)/i);
      return match ? decodeURIComponent(match[1]) : `RWK_Auswertung_Klasse${klassenId}.pdf`;
    })();
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = dispositionFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
  downloadAuswertungSaison: async (saisonId) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/pdf/auswertung/saison/${saisonId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const dispositionFilename = (() => {
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename\*?=(?:UTF-8''|")?([^;"']+)/i);
      return match ? decodeURIComponent(match[1]) : `RWK_Auswertung_Saison${saisonId}.pdf`;
    })();
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = dispositionFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
  downloadWettkampfErgebnis: async (wettkampfId) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/pdf/wettkampf/${wettkampfId}/ergebnis/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const dispositionFilename = (() => {
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename\*?=(?:UTF-8''|")?([^;"']+)/i);
      return match ? decodeURIComponent(match[1]) : `RWK_Ergebnis_${wettkampfId}.pdf`;
    })();
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = dispositionFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
  downloadWettkampfBerichtBlanko: async (wettkampfId) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/pdf/wettkampf/${wettkampfId}/blanko/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const dispositionFilename = (() => {
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename\*?=(?:UTF-8''|")?([^;"']+)/i);
      return match ? decodeURIComponent(match[1]) : `RWK_Wettkampfbericht_${wettkampfId}.pdf`;
    })();
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = dispositionFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
  downloadMannschaftTerminePdf: async (mannschaftsId) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/pdf/mannschaft/${mannschaftsId}/termine/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const dispositionFilename = (() => {
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename\*?=(?:UTF-8''|")?([^;"']+)/i);
      return match ? decodeURIComponent(match[1]) : `RWK_Termine_${mannschaftsId}.pdf`;
    })();
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = dispositionFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
  downloadMannschaftTermineIcs: async (mannschaftsId) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/pdf/mannschaft/${mannschaftsId}/termine/ical`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const dispositionFilename = (() => {
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename\*?=(?:UTF-8''|")?([^;"']+)/i);
      return match ? decodeURIComponent(match[1]) : `RWK_Termine_${mannschaftsId}.ics`;
    })();
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = dispositionFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
  downloadSaisonVereinTermineIcs: async (saisonId, vereinsId) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/pdf/saison/${saisonId}/verein/${vereinsId}/termine/ical`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const dispositionFilename = (() => {
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename\*?=(?:UTF-8''|")?([^;"']+)/i);
      return match ? decodeURIComponent(match[1]) : `RWK_Termine_Saison_${saisonId}_Verein_${vereinsId}.ics`;
    })();
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = dispositionFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
  downloadSaisonVereinTermineZip: async (saisonId, vereinsId) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE}/pdf/saison/${saisonId}/verein/${vereinsId}/termine/zip`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const dispositionFilename = (() => {
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename\*?=(?:UTF-8''|")?([^;"']+)/i);
      return match ? decodeURIComponent(match[1]) : `RWK_Termine_Saison_${saisonId}_Verein_${vereinsId}.zip`;
    })();
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = dispositionFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
  downloadMahnung: async (rechnungId, stufe, rechnungsnummer, faelligkeitTage = 14) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(
      `${API_BASE}/pdf/rechnung/${rechnungId}/mahnung/${stufe}/download?faelligkeitTage=${faelligkeitTage}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    const stufenLabel = stufe === 'erinnerung' ? 'Zahlungserinnerung' : stufe === 'mahnung1' ? '1Mahnung' : '2Mahnung';
    a.download = `RWK_${stufenLabel}_${rechnungsnummer || rechnungId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
    // Cache invalidieren, damit der nächste Rechnungs-GET den aktualisierten Zeitstempel zeigt
    invalidateGetCache('Rechnungen');
  },
  deleteMeldungPdf: (saisonId, vereinsId) => {
    const token = localStorage.getItem('authToken');
    return fetch(`${API_BASE}/pdf/meldung/saison/${saisonId}/verein/${vereinsId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (res) => { if (!res.ok) { const text = await res.text(); throw new Error(text || `HTTP ${res.status}`); } });
  },
};

// ===== Dateien (File storage) =====

function uploadFormData(endpoint, formData) {
  const token = localStorage.getItem('authToken');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(endpoint, {
    method: 'POST',
    headers,
    body: formData,
  }).then(handleResponse).then((result) => {
    invalidateGetCache();
    return result;
  });
}

export const Dateien = {
  getOrdnerBaum: () => getRequest(`${API_BASE}/Dateien/ordner`),
  createOrdner: (data) => postRequest(`${API_BASE}/Dateien/ordner/create`, data),
  updateOrdner: (data) => postRequest(`${API_BASE}/Dateien/ordner/update`, data),
  deleteOrdner: (data) => postRequest(`${API_BASE}/Dateien/ordner/delete`, data),
  uploadDatei: (formData) => uploadFormData(`${API_BASE}/Dateien/upload`, formData),
  updateDatei: (data) => postRequest(`${API_BASE}/Dateien/update`, data),
  deleteDatei: (data) => postRequest(`${API_BASE}/Dateien/delete`, data),

  /**
   * Teilbarer Link zur Download-Seite der Web-UI. Erwartet den URL-Namen
   * (Pflicht bei Dokumenten ohne Anmeldepflicht).
   */
  getDownloadUrl: (urlName) => `${window.location.origin}/download/${encodeURIComponent(urlName)}`,

  // --- Öffentlich: Zugriff ausschliesslich über den URL-Namen, ohne Anmeldung ---
  oeffentlich: {
    /** Metadaten für die Download-Seite. */
    getInfo: (urlName) =>
      getRequest(`${API_BASE}/Dateien/oeffentlich/${encodeURIComponent(urlName)}/info`, { cache: false }),
    /** Datei-Bytes zur Inline-Anzeige (Bildvorschau); kein Protokolleintrag. */
    previewBlob: (urlName) => fetchDateiBlob(`${API_BASE}/Dateien/oeffentlich/${encodeURIComponent(urlName)}/preview`),
    /** Startet den Download im Browser. */
    download: (urlName, dateiname) =>
      speichereDateiBlob(`${API_BASE}/Dateien/oeffentlich/${encodeURIComponent(urlName)}/download`, dateiname),
  },

  // --- In-App: Zugriff per Id, nur für anmeldepflichtige Dokumente ---
  previewBlobById: (dateiId) => fetchDateiBlob(`${API_BASE}/Dateien/${dateiId}/preview`),
  downloadById: (dateiId, dateiname) => speichereDateiBlob(`${API_BASE}/Dateien/${dateiId}/download`, dateiname),

  /** Lädt einen kompletten Ordner (inkl. Unterordner) als ZIP herunter. */
  downloadOrdnerZip: (ordnerId, ordnerName) =>
    speichereDateiBlob(`${API_BASE}/Dateien/ordner/${ordnerId}/zip`, `${ordnerName || 'Ordner'}.zip`),

  /**
   * Bequemer Wrapper: wählt anhand des Datei-Objekts den richtigen Weg
   * (öffentlicher URL-Name, sonst Id).
   */
  downloadDatei: (datei, dateiname) => (datei && datei.urlName
    ? Dateien.oeffentlich.download(datei.urlName, dateiname || datei.originalerDateiname)
    : Dateien.downloadById(datei.dateiId, dateiname || datei.originalerDateiname)),
  getPreviewBlob: (datei) => (datei && datei.urlName
    ? Dateien.oeffentlich.previewBlob(datei.urlName)
    : Dateien.previewBlobById(datei.dateiId)),

  getLogs: (dateiId) => getRequest(`${API_BASE}/Dateien/${dateiId}/logs`, { cache: false }),
  getAllLogs: () => getRequest(`${API_BASE}/Dateien/logs`, { cache: false }),
};

async function fetchDateiBlob(url) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.blob();
}

async function speichereDateiBlob(url, dateiname) {
  const blob = await fetchDateiBlob(url);
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = dateiname || 'download';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

// ===== Sicherheitsprotokoll (Security audit log, Admins only) =====
export const Sicherheitsprotokoll = {
  /**
   * Lädt eine Seite des Protokolls (neueste zuerst).
   * Filter: ereignis, schuetzenId, von/bis (ISO-Zeitstempel), suche.
   * Nicht gecacht – ein Protokoll soll immer den aktuellen Stand zeigen.
   */
  get: ({ ereignis, schuetzenId, von, bis, suche, anzahl = 100, offset = 0 } = {}) => {
    const params = new URLSearchParams();
    if (ereignis) params.append('ereignis', ereignis);
    if (schuetzenId) params.append('schuetzenId', schuetzenId);
    if (von) params.append('von', von);
    if (bis) params.append('bis', bis);
    if (suche) params.append('suche', suche);
    params.append('anzahl', anzahl);
    params.append('offset', offset);
    // Query-String ausserhalb des Templates anhaengen, damit
    // check:api-contract den Pfad der OpenAPI-Operation zuordnen kann.
    const qs = params.toString();
    return getRequest(`${API_BASE}/Sicherheitsprotokoll` + (qs ? `?${qs}` : ''), { cache: false });
  },
};

// ===== Aktive Benutzer (accounts with API requests in the last minutes, Admins only) =====
export const AktiveBenutzer = {
  /** Nicht gecacht – die Liste soll immer den aktuellen Stand zeigen. */
  get: () => getRequest(`${API_BASE}/AktiveBenutzer`, { cache: false }),
};

// ===== Änderungsprotokoll (change log: who changed what from/to, Admins only) =====
export const Aenderungsprotokoll = {
  /**
   * Lädt eine Seite des Änderungsprotokolls (neueste zuerst).
   * Filter: aktion (erstellt/geaendert/geloescht), tabelle, datensatzId, schuetzenId (Akteur),
   * sicherheitsrelevant (true/false), von/bis (ISO-Zeitstempel), suche.
   * Nicht gecacht – ein Protokoll soll immer den aktuellen Stand zeigen.
   */
  get: ({
    aktion, tabelle, datensatzId, schuetzenId, sicherheitsrelevant, von, bis, suche, anzahl = 100, offset = 0,
  } = {}) => {
    const params = new URLSearchParams();
    if (aktion) params.append('aktion', aktion);
    if (tabelle) params.append('tabelle', tabelle);
    if (datensatzId) params.append('datensatzId', datensatzId);
    if (schuetzenId) params.append('schuetzenId', schuetzenId);
    if (sicherheitsrelevant !== undefined) params.append('sicherheitsrelevant', sicherheitsrelevant);
    if (von) params.append('von', von);
    if (bis) params.append('bis', bis);
    if (suche) params.append('suche', suche);
    params.append('anzahl', anzahl);
    params.append('offset', offset);
    // Query-String ausserhalb des Templates anhaengen, damit
    // check:api-contract den Pfad der OpenAPI-Operation zuordnen kann.
    const qs = params.toString();
    return getRequest(`${API_BASE}/Aenderungsprotokoll` + (qs ? `?${qs}` : ''), { cache: false });
  },
};

// ===== ScopedBerechtigungen (Scoped role assignments per Verein/Mannschaft) =====
export const ScopedBerechtigungen = {
  getAll: () => getRequest(`${API_BASE}/ScopedBerechtigungen`),
  getBySchuetze: (schuetzenId) => getRequest(`${API_BASE}/ScopedBerechtigungen/schuetze/${schuetzenId}`),
  create: (data) => postRequest(`${API_BASE}/ScopedBerechtigungen/create`, data),
  delete: (data) => postRequest(`${API_BASE}/ScopedBerechtigungen/delete`, data),
};

/**
 * Export a utility for batch operations or custom requests
 */
export const API = {
  get: getRequest,
  post: postRequest,
  delete: deleteRequest,
  clearCache: () => invalidateGetCache(),
  invalidateCache: (match) => invalidateGetCache(match),
  baseUrl: API_BASE,
  authUrl: AUTH_BASE,
  handleResponse,
};
